/**
 * Config "vetrina" sezione #esperienze (home).
 *
 * Per categoria l'admin decide:
 *  - mode:   quali card mostrare in evidenza
 *            'premium'      → scelte a mano (listings.is_premium)
 *            'most_booked'  → auto, ordinate per n° prenotazioni confermate
 *  - label:  testo del badge sulle card (es. "Premium", "Più prenotate")
 *  - count:  quante card alimentano la vetrina (2-8). >4 → carosello (max 4 a vista).
 *  - period: finestra per "most_booked" (solo se mode='most_booked')
 *
 * Storage: settings.key = 'esperienze_showcase_config', value JSON keyed by categoria.
 */

export const SHOWCASE_SETTINGS_KEY = 'esperienze_showcase_config';

export type ShowcaseMode = 'premium' | 'most_booked';
export type ShowcasePeriod = 'all' | '90d' | '30d';

export interface ShowcaseCategoryConfig {
  mode: ShowcaseMode;
  label: string;
  count: number; // 2..8
  period: ShowcasePeriod;
}

export type ShowcaseConfig = Record<string, ShowcaseCategoryConfig>;

export const SHOWCASE_COUNT_MIN = 2;
export const SHOWCASE_COUNT_MAX = 8;

/** Default applicato a ogni categoria priva di config esplicita. */
export const DEFAULT_SHOWCASE: ShowcaseCategoryConfig = {
  mode: 'premium',
  label: 'Premium',
  count: 4,
  period: 'all',
};

const MODES: ShowcaseMode[] = ['premium', 'most_booked'];
const PERIODS: ShowcasePeriod[] = ['all', '90d', '30d'];

/**
 * Label disponibili PER MODALITÀ (combo in admin). Ogni mode ha il suo set;
 * la prima è il default. Il badge in home mostra la label scelta.
 */
export const SHOWCASE_LABEL_OPTIONS: Record<ShowcaseMode, string[]> = {
  premium: ['Premium', 'In evidenza', 'Top scelta', 'Consigliati'],
  most_booked: ['Più prenotate', 'I più richiesti', 'Bestseller', 'Di tendenza'],
};

/** Label di default per una modalità (prima opzione del suo set). */
export function defaultLabelForMode(mode: ShowcaseMode): string {
  return SHOWCASE_LABEL_OPTIONS[mode][0];
}

function clampCount(n: unknown): number {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return DEFAULT_SHOWCASE.count;
  return Math.min(SHOWCASE_COUNT_MAX, Math.max(SHOWCASE_COUNT_MIN, v));
}

/** Normalizza un singolo entry, riempiendo i campi mancanti col default. */
export function normalizeShowcaseEntry(raw: unknown): ShowcaseCategoryConfig {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const mode = MODES.includes(o.mode as ShowcaseMode) ? (o.mode as ShowcaseMode) : DEFAULT_SHOWCASE.mode;
  const period = PERIODS.includes(o.period as ShowcasePeriod) ? (o.period as ShowcasePeriod) : DEFAULT_SHOWCASE.period;
  // Label legata alla modalità: dev'essere una delle opzioni del mode scelto.
  // Valore assente/non valido → default del mode (prima opzione).
  const rawLabel = typeof o.label === 'string' ? o.label.trim() : '';
  const label = SHOWCASE_LABEL_OPTIONS[mode].includes(rawLabel) ? rawLabel : defaultLabelForMode(mode);
  return { mode, label, count: clampCount(o.count), period };
}

/** Parsa il value grezzo della settings in una config validata. */
export function parseShowcaseConfig(value: unknown): ShowcaseConfig {
  if (!value || typeof value !== 'object') return {};
  const out: ShowcaseConfig = {};
  for (const [cat, entry] of Object.entries(value as Record<string, unknown>)) {
    if (!cat) continue;
    out[cat] = normalizeShowcaseEntry(entry);
  }
  return out;
}

/** Config effettiva per una categoria (merge col default). */
export function getShowcaseForCategory(cfg: ShowcaseConfig | null | undefined, categoryId: string): ShowcaseCategoryConfig {
  return cfg?.[categoryId] ?? DEFAULT_SHOWCASE;
}
